/**
 * The null renderer (`?rhi=null`), as Unreal's `-nullrhi` runs automation on
 * build machines: the engine and editor run in full, and nothing is drawn.
 * CI runs the editor's UI tests on it (docs/planning/06-e2e-on-ci.md), where a
 * GPU-less runner would otherwise draw every frame in software.
 *
 * It differs from the Null backend it extends, which stands in when there's
 * no GPU at all (`?rhi=none`, R-1): the viewport keeps a canvas, never drawn
 * to, so the editor lays out and takes input as it does with a GPU. And it
 * won't pretend to have pixels: reading one fails, so a test that needs
 * drawing says so (`@renderer`) instead of passing on blank pixels.
 */
import type { AssetRegistry } from "../assets/AssetRegistry";
import { NullRenderBackend } from "./NullRenderBackend";
import type { RenderBackendFactory } from "./RenderBackend";

export class HeadlessRenderBackend extends NullRenderBackend {
  override readonly canvas: HTMLCanvasElement;

  constructor(canvas: HTMLCanvasElement) {
    super({ api: "Null", adapter: "null renderer (?rhi=null)" });
    this.canvas = canvas;
  }

  override readPixel(_x: number, _y: number): Promise<readonly [number, number, number, number]> {
    return Promise.reject(
      new Error("The null renderer draws nothing: a test that reads pixels is tagged @renderer"),
    );
  }
}

/**
 * The null renderer's factory. Models and textures load as bytes, since
 * decoding them is a renderer's job; the level streams as it does with one.
 */
export function headlessBackendFactory(
  createCanvas: () => HTMLCanvasElement = () => document.createElement("canvas"),
): RenderBackendFactory {
  return (context) => {
    loadAsBytes(context.assets);
    return Promise.resolve(new HeadlessRenderBackend(createCanvas()));
  };
}

function loadAsBytes(assets: AssetRegistry): void {
  assets.registerLoader("gltf", (bytes) => Promise.resolve(bytes as never));
  assets.registerLoader("texture", (bytes) => Promise.resolve(bytes as never));
  assets.registerLoader("panorama", (bytes) => Promise.resolve(bytes as never));
}
